import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import QRColumn from './QRColumn';
import NewModelInfoColumn from './NewModelInfoColumn';
import NewModelPriceTag from './NewModelPriceTag';
import { buildLandingUrl } from './utils/landingUrl';
import { t } from './translations';
import './NewModelPrintTemplate.css';

/** New-model QR sharp-edged. Horizontal — 340px (компактнее, info-col
 * рядом с QR). Vertical — 440px (есть место в высоту, QR крупнее для
 * сканируемости с расстояния). */
const NEW_MODEL_QR_SIZE_H = 340;
const NEW_MODEL_QR_SIZE_V = 400;
const NEW_MODEL_QR_SHAPE = 'sharp';

/** Default/min/step sizes for the title (px). */
const TITLE_MAX_SIZE = 40;
const TITLE_MIN_SIZE = 24;
const TITLE_STEP = 1;
const TITLE_MAX_LINES = 2;

const makeFormatPrice = (language) => (price) => {
  if (!price || price === 0) return t('contact', language);
  return `€${price.toLocaleString()}`;
};

/**
 * Print template for the Autodis OMODA/JAECOO showroom (issue #884).
 *
 * Layout:
 * - Horizontal: header row (title слева, price справа) + divider →
 *   content row (QR | InfoCol).
 * - Vertical: title сверху, content (QR row + info column), price-bottom
 *   с divider'ом снизу. Достигается через CSS `display: contents` на
 *   header в портрете — title и price-bottom становятся прямыми children
 *   template-flex-column и расставляются order'ом.
 *
 * @param {object} props
 * @param {object} props.car — Car object (DealerCarDto, must have `isNewModel: true`).
 * @param {'horizontal' | 'vertical'} [props.orientation='horizontal']
 * @param {'fr'|'en'|'de'|'lu'} [props.language='fr']
 * @param {'full'|'qr-only'|'catalog-qr'} [props.mode='full'] — режим
 *   шаблона. catalog-qr заменяет URL QR-кода на общий salon Welcome URL
 *   `${dealerSiteUrl}/?widget-view=catalog` (#1022). Car-specific info
 *   при этом всё равно скрывается родительским CarPrintPage (как и при
 *   qr-only) — этот компонент только подаёт корректный URL в QR.
 * @param {() => void} [props.onQrReady]
 */
const NewModelPrintTemplate = ({
  car,
  orientation = 'horizontal',
  language = 'fr',
  mode = 'full',
  onQrReady,
}) => {
  const cleanMake = car?.make?.replace(/\*/g, '') || car?.make || '';
  const cleanModel = car?.model?.replace(/\*/g, '') || car?.model || '';
  const displayTitle = car?.name || `${cleanMake} ${cleanModel}`.trim();

  const landingUrl = useMemo(() => buildLandingUrl(car, mode), [car, mode]);
  const formatPrice = useMemo(() => makeFormatPrice(language), [language]);
  const qrSize = orientation === 'vertical' ? NEW_MODEL_QR_SIZE_V : NEW_MODEL_QR_SIZE_H;

  // Auto-fit title в 2 строки (длинные имена моделей типа "JAECOO 7 SHS-H
  // Premium" не должны вылезать на третью строку и ломать высоту header'а).
  // useLayoutEffect — синхронно до paint, без flicker.
  const titleRef = useRef(null);
  const [titleSize, setTitleSize] = useState(TITLE_MAX_SIZE);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el || !displayTitle) return;
    const measureLines = () => el.getClientRects().length;
    let size = TITLE_MAX_SIZE;
    el.style.fontSize = `${size}px`;
    if (measureLines() <= TITLE_MAX_LINES) {
      el.style.fontSize = '';
      if (titleSize !== TITLE_MAX_SIZE) setTitleSize(TITLE_MAX_SIZE);
      return;
    }
    while (measureLines() > TITLE_MAX_LINES && size > TITLE_MIN_SIZE) {
      size -= TITLE_STEP;
      el.style.fontSize = `${size}px`;
    }
    if (size !== titleSize) setTitleSize(size);
    el.style.fontSize = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTitle, orientation]);

  if (!car) return null;

  const orientationClass = orientation === 'vertical' ? ' print-template--vertical' : '';

  return (
    <div className={`print-template print-template--new-model${orientationClass}`}>
      {/* Header — title и price-bottom. В horizontal flex-row с divider
          снизу. В vertical CSS делает header прозрачным (display: contents),
          и order'ом расставляет title сверху, content между, price-bottom
          снизу с собственным divider'ом. */}
      <div className="print-template__header">
        <h1
          ref={titleRef}
          className="print-template__title"
          style={{ fontSize: `${titleSize}px` }}
        >
          {displayTitle}
        </h1>
        <NewModelPriceTag
          price={car.price}
          promoPrice={car.promoPrice}
          formatPrice={formatPrice}
          language={language}
        />
      </div>

      <div className="print-template__content">
        <QRColumn
          url={landingUrl}
          size={qrSize}
          shape={NEW_MODEL_QR_SHAPE}
          language={language}
          onReady={onQrReady}
        />
        <NewModelInfoColumn car={car} language={language} />
      </div>
    </div>
  );
};

export default NewModelPrintTemplate;
